import React, { useState, useEffect, useCallback } from 'react';
import { create } from 'zustand';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../App';
import { moFamilyStore } from '../stores/moFamilyStore';

import type { MoFamily, MoFamilyMember, MoFamilySlot, VideoFile, MoFamilyStore, InvitationDetails } from './mofamily/types';
import { FamilyListView } from './mofamily/FamilyListView';
import { FamilyDetailView } from './mofamily/FamilyDetailView';
import NegotiationModal from './mofamily/NegotiationModal';

// Zustand Store
const useMoFamilyStore = create<MoFamilyStore>((set, get) => ({
 families: [],
 selectedFamily: null,
 familyMembers: [],
 familySlots: [],
 videos: [],
 isLoading: false,
 error: null,

 setFamilies: (families) => set({ families }),
 setSelectedFamily: (family) => {
 console.log('Local store setSelectedFamily called:', family);
 set({ selectedFamily: family });
 },
 setFamilyMembers: (members) => set({ familyMembers: members }),
 setFamilySlots: (slots) => set({ familySlots: slots }),
 setVideos: (videos) => set({ videos }),
 setLoading: (loading) => set({ isLoading: loading }),
 setError: (error) => set({ error }),

 updateSlotDisplay: (familyHash, totalSlots) => {
 set(state => ({
 families: state.families.map(f =>
 f.moFamilyHash === familyHash ? { ...f, totalSlots } : f
 )
 }));
 },

 handleWebSocketMessage: (message) => {
 console.log('MoFamily WebSocket message:', message);

 // Don't handle slot messages here - let moFamilyStore handle them
 if (message.type === 'slot_added' || message.type === 'slot_removed' || message.type === 'slot_updated') {
 return;
 }
 }
}));

const MoFamilyPage: React.FC = () => {
 const { moFamilyHash } = useParams<{ moFamilyHash?: string }>();
 const navigate = useNavigate();
 const { user } = useAppStore();
 const {
 families, selectedFamily, familyMembers, familySlots, videos: familyVideos,
 isLoading, error, setFamilies, setSelectedFamily, setFamilyMembers,
 setFamilySlots, setVideos: setFamilyVideos, setLoading, setError, handleWebSocketMessage
 } = useMoFamilyStore();

 const [hierarchyData, setHierarchyData] = useState<any>(null);
 const [myVideos, setMyVideos] = useState<VideoFile[]>([]);
 const [parentFamily, setParentFamily] = useState<any>(null);
 const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
 const [userOwnedFamilies, setUserOwnedFamilies] = useState<MoFamily[]>([]);
 const [showError, setShowError] = useState(false);
 const [isProcessingSlot, setIsProcessingSlot] = useState(false);
 const [childFamilies, setChildFamilies] = useState<any[]>([]);

 // Negotiation modal states
 const [showNegotiationModal, setShowNegotiationModal] = useState(false);
 const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null);

 // Get invitation code from URL query params
 const urlParams = new URLSearchParams(window.location.search);
 const invitationCodeFromUrl = urlParams.get('invitation');

 // Load families
 const loadFamilies = useCallback(async () => {
 setLoading(true);
 try {
 const response = await fetch('/mofamily/my-families', {
 credentials: 'include'
 });

 if (response.ok) {
 const data = await response.json();
 setFamilies(data.data || []);

 // Store user's owned/operated families
 const userFamilies = (data.data || []).filter((f: MoFamily) =>
 f.role === 'owner' || f.role === 'operator'
 );
 setUserOwnedFamilies(userFamilies);

 // Check if default family exists and hide button
 const hasDefaultFamily = (data.data || []).some((f: MoFamily) =>
 f.description?.includes('자동 생성') ||
 f.description?.includes('기본 모패밀리') ||
 f.name?.includes('기본 모패밀리') ||
 f.maxMembers === 1
 );

 if (hasDefaultFamily) {
 const defaultBtn = document.getElementById('createDefaultBtn');
 if (defaultBtn) {
 defaultBtn.style.display = 'none';
 }
 }
 } else {
 setError('모패밀리 목록을 불러오지 못했습니다.');
 }
 } catch (error) {
 setError('네트워크 오류가 발생했습니다.');
 } finally {
 setLoading(false);
 }
 }, [setFamilies, setLoading, setError]);

 // Load family details
 const loadFamilyDetails = useCallback(async (familyHash: string) => {
 console.log('Loading family details for:', familyHash);
 console.trace('loadFamilyDetails called from:');
 setLoading(true);
 try {
 // Load family info
 const familyResponse = await fetch(`/mofamily/${familyHash}`, {
 credentials: 'include'
 });

 console.log('Family response status:', familyResponse.status);

 if (!familyResponse.ok) {
 console.error('Failed to load family details, status:', familyResponse.status);
 navigate('/mofamily');
 return;
 }

 const familyData = await familyResponse.json();
 console.log('Loaded family data:', familyData.data);
 const family = familyData.data;
 setSelectedFamily(family);

 // Also update moFamilyStore
 moFamilyStore.getState().updateFamily(familyHash, family);
 moFamilyStore.getState().setSelectedFamily(familyHash);

 // Ensure we have the family in store's families map
 const familiesMap = new Map(moFamilyStore.getState().families);
 familiesMap.set(familyHash, family);
 moFamilyStore.setState({ families: familiesMap });

 // Load members
 const membersResponse = await fetch(`/mofamily/${familyHash}/members`, {
 credentials: 'include'
 });

 if (membersResponse.ok) {
 const membersData = await membersResponse.json();
 setFamilyMembers(membersData.data || []);
 }

 // Load slot sets (multi-slot system)
 const slotsResponse = await fetch(`/mofamily/${familyHash}/slot-sets`, {
 credentials: 'include'
 });

 if (slotsResponse.ok) {
 const slotsData = await slotsResponse.json();
 const sets = slotsData.data.sets;
 moFamilyStore.getState().setSlotSets(familyHash, sets);
 // Legacy: extract active set slots for local state
 const activeSet = sets.find((s: any) => s.isActive) || sets[0];
 setFamilySlots(Array.isArray(activeSet?.slots) ? activeSet.slots : []);
 }

 // Load relationships (child families)
 const relationshipsResponse = await fetch(`/mofamily/${familyHash}/relationships`, {
 credentials: 'include'
 });

 if (relationshipsResponse.ok) {
 const relationshipsData = await relationshipsResponse.json();
 console.log('Relationships data:', relationshipsData);
 // Filter for direct children only
 const children = relationshipsData.data?.filter((r: any) =>
 r.relationType === 'child' && r.level === 0
 ) || [];
 setChildFamilies(children);
 setHierarchyData(relationshipsData.data);

 // Create hierarchy in moFamilyStore for the current family
 const currentFamilyNode = {
 moFamilyHash: familyHash,
 name: family.name,
 totalSlots: family.totalSlots || 0,
 ownedSlots: family.totalSlots || 0,
 children: [],
 isOwner: family.isOwner || family.role === 'owner',
 sharingType: undefined as 'dedicated' | 'shared' | undefined
 };
 moFamilyStore.getState().setHierarchy([currentFamilyNode]);

 // Store each family in moFamilyStore and load their slots
 for (const rel of relationshipsData.data || []) {
 if (rel.familyHash && rel.familyName) {
 // Load full family info
 try {
 const familyResp = await fetch(`/mofamily/${rel.familyHash}`, {
 credentials: 'include'
 });

 if (familyResp.ok) {
 const familyData = await familyResp.json();
 moFamilyStore.getState().updateFamily(rel.familyHash, familyData.data);
 } else {
 // Fallback to basic info
 moFamilyStore.getState().updateFamily(rel.familyHash, {
 moFamilyHash: rel.familyHash,
 name: rel.familyName,
 totalSlots: rel.totalSlots || 0,
 });
 }
 } catch (error) {
 console.error(`Error loading family info for ${rel.familyName}:`, error);
 }

 // Load slot sets for each family (multi-slot system)
 try {
 const slotsResp = await fetch(`/mofamily/${rel.familyHash}/slot-sets`, {
 credentials: 'include'
 });

 if (slotsResp.ok) {
 const slotsData = await slotsResp.json();
 moFamilyStore.getState().setSlotSets(rel.familyHash, slotsData.data.sets);
 }
 } catch (error) {
 console.error(`Error loading slots for ${rel.familyName}:`, error);
 }
 }
 }
 }

 // Load parent family information
 try {
 const parentResponse = await fetch(`/mofamily/${familyHash}/parent`, {
 credentials: 'include'
 });

 if (parentResponse.ok) {
 const parentData = await parentResponse.json();
 setParentFamily(parentData.data);
 } else {
 setParentFamily(null);
 }
 } catch (error) {
 console.error('Error loading parent family:', error);
 setParentFamily(null);
 }

 // Load videos for this family
 const isOwnerOrOperator = familyData.data?.isOwner || familyData.data?.role === 'owner' || familyData.data?.role === 'operator';
 if (isOwnerOrOperator) {
 const videosResponse = await fetch(`/mofile/family/${familyHash}`, {
 credentials: 'include'
 });

 if (videosResponse.ok) {
 const videosData = await videosResponse.json();
 setFamilyVideos(videosData.data || []);
 } else {
 setFamilyVideos([]);
 }
 }
 } catch (error) {
 console.error('loadFamilyDetails error:', error);
 setError('상세 정보를 불러오지 못했습니다.');
 } finally {
 setLoading(false);
 setHasInitiallyLoaded(true);
 }
 }, [navigate, setSelectedFamily, setFamilyMembers, setFamilySlots, setLoading, setError]);

 // Create family
 const createFamily = async (name: string, description: string) => {
 try {
 const response = await fetch('/mofamily/create', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({
 name,
 description: description || undefined,
 maxMembers: 50
 })
 });

 if (response.ok) {
 await loadFamilies();
 } else {
 const error = await response.json();
 setError(error.error || '모패밀리 생성에 실패했습니다.');
 }
 } catch (error) {
 setError('모패밀리 생성 중 오류가 발생했습니다.');
 }
 };

 // Create default family
 const createDefaultFamily = async () => {
 try {
 const response = await fetch('/mofamily/create-default', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include'
 });

 const data = await response.json();
 console.log('Create default family response:', { status: response.status, data });

 if (response.ok && data.success) {
 alert('기본 모패밀리가 생성되었습니다!');
 await loadFamilies();
 } else {
 throw new Error(data.error || '기본 모패밀리 생성에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error creating default family:', error);
 alert(error instanceof Error ? error.message : '기본 모패밀리 생성 중 오류가 발생했습니다.');
 }
 };

 // Load invitation details for negotiation
 const loadInvitationDetails = async (invitationCode: string) => {
 try {
 const response = await fetch(`/mofamily/invitation/${invitationCode}`, {
 credentials: 'include'
 });

 if (response.ok) {
 const data = await response.json();
 setInvitationDetails(data.data);
 setShowNegotiationModal(true);
 } else {
 alert('초대 정보를 불러올 수 없습니다.');
 }
 } catch (error) {
 console.error('Failed to load invitation details:', error);
 alert('초대 정보를 불러오는 중 오류가 발생했습니다.');
 }
 };

 // Load my videos
 const loadMyVideos = async () => {
 try {
 const response = await fetch('/mofile/my-files', {
 credentials: 'include'
 });

 if (response.ok) {
 const data = await response.json();
 setMyVideos(data.data || []);
 }
 } catch (error) {
 console.error('내 영상 로드 오류:', error);
 setMyVideos([]);
 }
 };

 // Load family videos only
 const loadFamilyVideos = async (familyHash: string) => {
 try {
 console.log('Loading family videos for:', familyHash);
 const videosResponse = await fetch(`/mofile/family/${familyHash}`, {
 credentials: 'include'
 });

 if (videosResponse.ok) {
 const videosData = await videosResponse.json();
 console.log('Family videos loaded:', videosData.data);
 setFamilyVideos(videosData.data || []);
 } else {
 console.error('Failed to load family videos, status:', videosResponse.status);
 setFamilyVideos([]);
 }
 } catch (error) {
 console.error('모패밀리 영상 로드 오류:', error);
 setFamilyVideos([]);
 }
 };

 // Load hierarchy data
 const loadHierarchy = async (familyHash: string) => {
 try {
 const relationshipsResponse = await fetch(`/mofamily/${familyHash}/relationships`, {
 credentials: 'include'
 });

 if (relationshipsResponse.ok) {
 const relationshipsData = await relationshipsResponse.json();
 console.log('Reloaded relationships data:', relationshipsData);

 const children = relationshipsData.data?.filter((r: any) =>
 r.relationType === 'child' && r.level === 0
 ) || [];
 setChildFamilies(children);
 setHierarchyData(relationshipsData.data);

 // Update hierarchy in moFamilyStore
 const currentFamilyNode = {
 moFamilyHash: familyHash,
 name: selectedFamily?.name || '',
 totalSlots: selectedFamily?.totalSlots || 0,
 ownedSlots: selectedFamily?.totalSlots || 0,
 children: [],
 isOwner: selectedFamily?.isOwner || selectedFamily?.role === 'owner',
 sharingType: undefined as 'dedicated' | 'shared' | undefined
 };
 moFamilyStore.getState().setHierarchy([currentFamilyNode]);
 }
 } catch (error) {
 console.error('Error loading hierarchy:', error);
 }
 };

 // Effects
 useEffect(() => {
 console.log('MoFamily useEffect - moFamilyHash:', moFamilyHash);
 if (moFamilyHash) {
 setHasInitiallyLoaded(false);
 loadFamilyDetails(moFamilyHash);
 } else {
 setSelectedFamily(null);
 setHasInitiallyLoaded(false);
 loadFamilies();
 }

 // Check for invitation code in URL params
 const urlParams = new URLSearchParams(window.location.search);
 const invitationCode = urlParams.get('invitation');
 if (invitationCode) {
 loadInvitationDetails(invitationCode);
 }
 }, [moFamilyHash]);

 // Load my videos when in detail view
 useEffect(() => {
 if (selectedFamily) {
 loadMyVideos();
 }
 }, [selectedFamily]);

 // Subscribe to hierarchy reload flag from moFamilyStore
 useEffect(() => {
 const unsubscribe = moFamilyStore.subscribe((state) => {
 if (state.hierarchyNeedsReload && selectedFamily) {
 console.log('Hierarchy needs reload - reloading hierarchy for', selectedFamily.moFamilyHash);
 loadHierarchy(selectedFamily.moFamilyHash);
 state.setHierarchyNeedsReload(false);
 }
 });

 return () => unsubscribe();
 }, [selectedFamily]);

 // Handle invitation code from URL
 useEffect(() => {
 if (invitationCodeFromUrl && !showNegotiationModal) {
 console.log('Loading invitation from URL:', invitationCodeFromUrl);
 loadInvitationDetails(invitationCodeFromUrl);

 // Clear the invitation parameter from URL to prevent reload
 const newUrl = window.location.pathname;
 window.history.replaceState({}, '', newUrl);
 }
 }, []);

 useEffect(() => {
 // Click outside handler for search dropdown
 const handleClickOutside = (e: MouseEvent) => {
 const target = e.target as HTMLElement;
 if (!target.closest('.search-container')) {
 // Search dropdown handled inside InviteTab
 }
 };

 // Hierarchy update handler
 const handleHierarchyUpdate = (e: CustomEvent) => {
 console.log('Hierarchy update event received:', e.detail);
 if (e.detail.familyHash && e.detail.familyHash === selectedFamily?.moFamilyHash) {
 loadHierarchy(e.detail.familyHash);
 }
 };

 document.addEventListener('click', handleClickOutside);
 window.addEventListener('hierarchyUpdate', handleHierarchyUpdate as EventListener);

 return () => {
 document.removeEventListener('click', handleClickOutside);
 window.removeEventListener('hierarchyUpdate', handleHierarchyUpdate as EventListener);
 };
 }, [selectedFamily]);

 // Show error if family hash is provided but no family is loaded
 useEffect(() => {
 let timer: NodeJS.Timeout;
 if (moFamilyHash && !selectedFamily && !isLoading && !isProcessingSlot && hasInitiallyLoaded) {
 console.log('Error condition met - will show error in 1s', { moFamilyHash, selectedFamily, isLoading, isProcessingSlot, hasInitiallyLoaded });
 timer = setTimeout(() => {
 console.log('Setting showError to true');
 setShowError(true);
 }, 1000);
 } else {
 setShowError(false);
 }
 return () => clearTimeout(timer);
 }, [moFamilyHash, selectedFamily, isLoading, isProcessingSlot, hasInitiallyLoaded]);

 // Show loading state while fetching family details
 if (moFamilyHash && isLoading) {
 return (
 <div>
 <div className="bg-white p-6">
 <div className="animate-pulse">
 <div className="h-8 bg-gray-200 w-1/3 mb-4"></div>
 <div className="h-4 bg-gray-200 w-1/2 mb-2"></div>
 <div className="h-4 bg-gray-200 w-1/4"></div>
 </div>
 </div>
 </div>
 );
 }

 if (showError) {
 return (
 <div>
 <div className="bg-red-50 text-red-800 p-4">
 <p>패밀리를 불러올 수 없습니다. 잠시 후 다시 시도해주세요.</p>
 <button
 onClick={() => navigate('/mofamily')}
 className="mt-2 text-blue-600 hover:text-blue-800"
 >
 목록으로 돌아가기
 </button>
 </div>
 </div>
 );
 }

 // Render detail view
 if (moFamilyHash && selectedFamily) {
 return (
 <>
 <FamilyDetailView
 selectedFamily={selectedFamily}
 hierarchyData={hierarchyData}
 familyVideos={familyVideos}
 myVideos={myVideos}
 parentFamily={parentFamily}
 userOwnedFamilies={userOwnedFamilies}
 onNavigate={(path) => navigate(path)}
 onFamilyUpdated={loadFamilyDetails}
 onFamilyVideosChanged={(h) => { loadFamilyVideos(h); loadMyVideos(); }}
 />

 {/* Negotiation Modal */}
 {showNegotiationModal && invitationDetails && (
 <NegotiationModal
 invitationDetails={invitationDetails}
 selectedFamily={selectedFamily}
 user={user}
 onClose={() => {
 setShowNegotiationModal(false);
 setInvitationDetails(null);
 }}
 onAccepted={() => {
 setShowNegotiationModal(false);
 setInvitationDetails(null);
 loadFamilies();
 }}
 />
 )}
 </>
 );
 }

 // Render list view
 return (
 <>
 <FamilyListView
 families={families}
 isLoading={isLoading}
 error={error}
 onNavigate={(path) => navigate(path)}
 onCreateFamily={createFamily}
 onCreateDefaultFamily={createDefaultFamily}
 />

 {/* Negotiation Modal (can appear on list view too via URL params) */}
 {showNegotiationModal && invitationDetails && (
 <NegotiationModal
 invitationDetails={invitationDetails}
 selectedFamily={selectedFamily}
 user={user}
 onClose={() => {
 setShowNegotiationModal(false);
 setInvitationDetails(null);
 }}
 onAccepted={() => {
 setShowNegotiationModal(false);
 setInvitationDetails(null);
 loadFamilies();
 }}
 />
 )}
 </>
 );
};

export default MoFamilyPage;
